import { createApp, watch, watchEffect } from "vue";
import { createRouter, createWebHistory } from "vue-router";
import { createAuth0, authGuard, useAuth0 } from "@auth0/auth0-vue";
import { library } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { fas } from "@fortawesome/free-solid-svg-icons";
import { far } from "@fortawesome/free-regular-svg-icons";
import { fab } from "@fortawesome/free-brands-svg-icons";
import App from "./App.vue";
import NotFoundPage from "./pages/NotFoundPage.vue"
import LandingPage from "./pages/LandingPage.vue";
import HomePage from "./pages/HomePage.vue";
import AdminPage from "./pages/AdminPage.vue";
import SnailMailPage from "./pages/SnailMail.vue";
import './registerServiceWorker';

const app = createApp(App);

const router = createRouter({
  history: createWebHistory(),
  routes: [
    {
      path: "/",
      name: "landing",
      component: LandingPage,
      meta: { title: "Blything | Welcome" }
    },
    {
      path: "/home",
      name: "home",
      component: HomePage,
      beforeEnter: authGuard,
      meta: { title: "Blything | Home" }
    },
    {
      path: "/admin",
      name: "admin",
      component: AdminPage,
      beforeEnter: authGuard,
      meta: { title: "Blything | Admin", requiresAdmin: true }
    },
    {
      path: "/snail-mail",
      name: "snailMail",
      component: SnailMailPage,
      alias: "/snailmail",
      meta: {
        title: "Snail Mail",
        hideLoader: true
      }
    },
    {
      path: "/:pathMatch(.*)*",
      name: "notFound",
      component: NotFoundPage,
      meta: { title: "Blything | Not Found" }
    },
  ],
});

// Added `async` here so `await` is allowed
router.beforeEach(async (to, from, next) => {
  // Correctly extract reactivity properties directly from useAuth0()
  const { isLoading, user, isAuthenticated } = useAuth0();

  // 1. Wait for Auth0 to finish loading the session from storage
  if (isLoading.value) {
  await new Promise((resolve) => {
    const stop = watchEffect(() => {
      if (!isLoading.value) {
        stop();
        resolve();
      }
    });
  });
}

  // 2. Read custom roles claim from the loaded user profile
  const roles = user.value?.['https://blything.co.uk/roles'] || [];
  const isAdmin = isAuthenticated.value && roles.includes('Admin');

  // 3. Admin Route Check
  if (to.meta.requiresAdmin && !isAdmin) {
    return next('/'); // Redirect non-admins to home
  }

  // 4. Update Document Title dynamically
  document.title = to.meta.title || "Blything";

  next();
});

library.add(fas, far, fab);
app.component("font-awesome-icon", FontAwesomeIcon);

// 1. Install Auth0 plugin first
app.use(
  createAuth0({
    domain: process.env.VUE_APP_AUTH0_DOMAIN,
    clientId: process.env.VUE_APP_AUTH0_CLIENT_ID,
    authorizationParams: {
      redirect_uri: window.location.origin,
      audience: process.env.VUE_APP_AUTH0_AUDIENCE,
    },
    useRefreshTokens: true,
    cacheLocation: "localstorage",
  })
);

// 2. Install Router SECOND (so authGuard can communicate with Auth0)
app.use(router);

// 3. Mount the app
app.mount("#app");